/**
 * 朗读分句：把长文本切成"每句一次 TTS 请求"的粒度。
 *
 * 分句粒度直接决定两件事：
 * 1. 首句出声延迟（句子越短，第一次 TTS 返回越快）
 * 2. 跳转精度（只能跳到句子边界）
 *
 * 所以既要按句末标点切，也要给出上下限：太短的碎片合并回上一句（避免"好的。"
 * 单独一次请求），太长的句子按次级标点强切（避免一句话几百字拖慢首播）。
 */

export interface SplitSentencesOptions {
  /** 单句最大长度，超过则按次级标点/空格强切 */
  maxLength?: number;
  /** 单句最小长度，不足则并入上一句 */
  minLength?: number;
}

const DEFAULT_MAX_LENGTH = 160;
const DEFAULT_MIN_LENGTH = 12;

// 句末标点（中英文），后面可能跟着引号/括号，一并归到本句
const SENTENCE_END_REGEX = /([.!?。！？…；;])(["'”’)）\]】》]*)/g;
// 次级标点：句子超长时的强切点
const CLAUSE_BREAK_REGEX = /([,，、:：—-])/g;

/**
 * 英文句点的歧义处理：`.` 既是句号也是小数点和缩写点。
 * 判定为句末的条件是「后面接空白 + 下一个词不是小写开头」，
 * 这样 3.5 / U.S. dollar / e.g. 都不会被误切。
 */
function isEnglishSentenceEnd(paragraph: string, dotIndex: number, endIndex: number): boolean {
  const prevChar = paragraph[dotIndex - 1];
  const nextChar = paragraph[endIndex];

  // 句点直接夹在字母数字之间：小数或域名，不是句末
  if (prevChar && nextChar && /[0-9A-Za-z]/.test(prevChar) && /[0-9A-Za-z]/.test(nextChar)) {
    return false;
  }

  // 段落结尾，一定是句末
  if (nextChar === undefined) return true;

  // 后面不是空白（如 "a.b"）不算句末
  if (!/\s/.test(nextChar)) return false;

  const nextNonSpace = paragraph.slice(endIndex).match(/\S/);
  if (!nextNonSpace) return true;

  // 下一个词以小写字母开头 → 上一句没结束（U.S. dollar、Inc. and…）
  return !/[a-z]/.test(nextNonSpace[0]);
}

/** 按句末标点切分单个段落，标点保留在句尾 */
function splitBySentenceEnd(paragraph: string): string[] {
  const chunks: string[] = [];
  let lastIndex = 0;

  SENTENCE_END_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SENTENCE_END_REGEX.exec(paragraph)) !== null) {
    const end = match.index + match[0].length;
    if (match[1] === '.' && !isEnglishSentenceEnd(paragraph, match.index, end)) {
      continue;
    }

    chunks.push(paragraph.slice(lastIndex, end));
    lastIndex = end;
  }

  if (lastIndex < paragraph.length) {
    chunks.push(paragraph.slice(lastIndex));
  }

  return chunks.map((chunk) => chunk.trim()).filter(Boolean);
}

/** 超长句子按次级标点切；仍然超长则按空格/硬截断切 */
function enforceMaxLength(sentence: string, maxLength: number): string[] {
  if (sentence.length <= maxLength) {
    return [sentence];
  }

  const pieces: string[] = [];
  let buffer = '';

  // 先按次级标点拆成短语（标点留在短语尾部）
  const clauses = sentence
    .split(CLAUSE_BREAK_REGEX)
    .reduce<string[]>((acc, part, index, arr) => {
      if (index % 2 === 1) return acc; // 分隔符本身由下面拼回
      const separator = arr[index + 1] ?? '';
      const clause = `${part}${separator}`.trim();
      if (clause) acc.push(clause);
      return acc;
    }, []);

  for (const clause of clauses) {
    if (clause.length > maxLength) {
      if (buffer) {
        pieces.push(buffer);
        buffer = '';
      }
      // 单个短语仍超长：按空格切，没有空格就硬截断（CJK 场景）
      let rest = clause;
      while (rest.length > maxLength) {
        const window = rest.slice(0, maxLength);
        const spaceIndex = window.lastIndexOf(' ');
        const cut = spaceIndex > maxLength * 0.5 ? spaceIndex + 1 : maxLength;
        pieces.push(rest.slice(0, cut).trim());
        rest = rest.slice(cut);
      }
      if (rest.trim()) buffer = rest.trim();
      continue;
    }

    const next = buffer ? `${buffer} ${clause}` : clause;
    if (next.length > maxLength) {
      pieces.push(buffer);
      buffer = clause;
    } else {
      buffer = next;
    }
  }

  if (buffer) pieces.push(buffer);
  return pieces.filter(Boolean);
}

/**
 * 把清洗后的纯文本切成适合逐句 TTS 的句子数组。
 * 输入应当先经过 stripMarkdownForSpeech 处理。
 */
export function splitSentences(
  text: string,
  options: SplitSentencesOptions = {},
): string[] {
  const { maxLength = DEFAULT_MAX_LENGTH, minLength = DEFAULT_MIN_LENGTH } = options;

  if (!text?.trim()) {
    return [];
  }

  const paragraphs = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const sentences: string[] = [];
  for (const paragraph of paragraphs) {
    for (const chunk of splitBySentenceEnd(paragraph)) {
      sentences.push(...enforceMaxLength(chunk, maxLength));
    }
  }

  // 过短的碎片并回上一句（合并后不超过 maxLength 才合并）
  const merged: string[] = [];
  for (const sentence of sentences) {
    const previous = merged[merged.length - 1];
    if (
      previous &&
      sentence.length < minLength &&
      previous.length + sentence.length + 1 <= maxLength
    ) {
      merged[merged.length - 1] = `${previous} ${sentence}`;
      continue;
    }
    merged.push(sentence);
  }

  return merged;
}
